import {
  OperationVariables,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';

import { GraphQLResult, getError, getTypeOrNull } from './apollo';

type QueryVariables = {
  name: string;
};

// Define a basic shape for GraphQL errors returned within the data object
type GraphQLErrorShape = {
  __typename: 'Error';
  message?: string | null;
};

type QueryHook<
  TData,
  TVariables extends OperationVariables = QueryVariables,
> = (
  options: QueryHookOptions<TData, TVariables> & {
    variables: TVariables;
    skip?: boolean;
  },
) => QueryResult<TData, TVariables>;

export type StandardGetHookOptions = {
  name: string;
  skip?: boolean;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
};

// TItem is the type of the data *at* dataPath
// TPath is the key under which TItem will be returned
type StandardGetHookResult<
  TData,
  TItem,
  TPath extends string,
  TVariables extends OperationVariables,
> = {
  [K in TPath]: TItem | undefined; // Use TItem here
} & {
  error?: Error;
  isLoading: boolean;
  refetch: QueryResult<TData, TVariables>['refetch'];
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
};

// Overload for without subscription
export function withStandardGetHook<
  // Constraint now includes potential GraphQLErrorShape
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  useQuery: QueryHook<TData, TVariables>,
  dataPath: TPath,
  queryOptions?: Pick<QueryHookOptions, 'fetchPolicy'>,
): (
  options: StandardGetHookOptions,
) => StandardGetHookResult<TData, TItem, TPath, TVariables>; // Return TItem

// Implementation
export function withStandardGetHook<
  // Constraint now includes potential GraphQLErrorShape
  TData extends { [K in TPath]?: TItem | GraphQLErrorShape | null | undefined },
  TItem,
  TPath extends string,
  TVariables extends OperationVariables & { name: string },
>(
  useQuery: QueryHook<TData, TVariables>,
  dataPath: TPath,
  queryOptions?: Pick<QueryHookOptions, 'fetchPolicy'>,
) {
  return (
    options: StandardGetHookOptions,
  ): StandardGetHookResult<TData, TItem, TPath, TVariables> => {
    const {
      data,
      error: networkError,
      loading,
      refetch,
      startPolling,
      stopPolling,
    } = useQuery({
      variables: { name: options.name } as TVariables,
      skip: options.skip,
      fetchPolicy: queryOptions?.fetchPolicy ?? options.fetchPolicy,
    });

    // Get the data directly from the path, type is inferred as TItem | null
    const typedData =
      getTypeOrNull(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;
    const appError =
      getError(data?.[dataPath] as GraphQLResult | null | undefined) ??
      undefined;

    return {
      // Ensure the returned value is TItem | undefined
      [dataPath]: typedData as TItem | undefined,
      error: appError ?? networkError,
      isLoading: loading,
      refetch,
      startPolling,
      stopPolling,
    } as StandardGetHookResult<TData, TItem, TPath, TVariables>;
  };
}
